import {
  Badge,
  Button,
  Collapse,
  Divider,
  Grid,
  Loader,
  Notification,
  Select,
  Table,
  MediaQuery,
  Modal,
  MultiSelect,
  Image,
  TextInput,
  Text,
  ThemeIcon,
  Tooltip,
  Group,
  Space,
} from "@mantine/core";
import { Check, Crown, Flag, InfoCircle } from "tabler-icons-react";
import { IconBrandWhatsapp, IconTrash } from "@tabler/icons-react";
import { WhatsAppLinkModal } from "./whatsapp-link-modal";
import {
  associateUserWithFleetOperator,
  deleteUser,
  downgradeToUser,
  upgradeToAdmin,
} from "../components/shared/hooks/user-management";
import React, { useEffect, useState, Fragment, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/shared";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  deleteDoc,
  onSnapshot,
  QuerySnapshot,
  DocumentData,
} from "firebase/firestore";
import { db } from "../firebase";
import { User, FuelTemplateNames } from "@captiva/shared-types";

const templateOptions = [
  { value: FuelTemplateNames.CAPTURED_REFUEL, label: "Refuel at roadside filling stations" },
  { value: FuelTemplateNames.CAPTURED_FUEL_TANK_ISSUE, label: "Refuel at your own onsite tanks" },
  { value: FuelTemplateNames.CAPTURED_BULK_FUEL_RECEIVED, label: "Receive bulk fuel deliveries" },
  { value: FuelTemplateNames.CAPTURED_FUEL_TANK_TRANSFER, label: "Transfer fuel from tank to tank" },
  { value: FuelTemplateNames.CAPTURED_TANK_LEVEL_READING, label: "Read tank dipstick levels" },
].sort((a, b) => a.label.localeCompare(b.label));

interface FleetOperatorEntry {
  id: string | null;
  fullName: string;
  [key: string]: unknown;
}

export interface WhatsAppIdentityEntry {
  id: string;
  phone: string;
  uid: string;
  companyId: string;
  verified: boolean;
  optedIn?: boolean;
  [key: string]: unknown;
}

interface UserTableProps {
  elements: User[];
  updateCallback: () => void;
  userId: string | undefined;
  companyId: string;
  isCompanyOwner?: boolean;
  companyOwner?: string | null;
  isAdmin?: boolean;
  whatsappIdentities?: WhatsAppIdentityEntry[];
  onWhatsappIdentitiesChange?: () => void;
}

export function UserTable({
  elements,
  updateCallback,
  userId,
  companyId,
  isCompanyOwner,
  companyOwner,
  isAdmin,
  whatsappIdentities,
  onWhatsappIdentitiesChange,
}: UserTableProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isOperatorsFetched, setIsOperatorsFetched] = useState(false);
  const [fleetOperators, setFleetOperators] = useState<FleetOperatorEntry[]>([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editFleetOperator, setEditFleetOperator] = useState<string | null>(null);
  const [editUserTemplates, setEditUserTemplates] = useState<string[]>([]);
  const [editUserTemplatesLoading, setEditUserTemplatesLoading] = useState(false);
  const [editUserTemplatesSaving, setEditUserTemplatesSaving] = useState(false);
  const [editUserTemplatesError, setEditUserTemplatesError] = useState("");
  const [editFlexTemplates, setEditFlexTemplates] = useState<string[]>([]);
  const [editFlexTemplatesLoading, setEditFlexTemplatesLoading] = useState(false);
  const [editFlexTemplatesSaving, setEditFlexTemplatesSaving] = useState(false);
  const [editFlexTemplatesError, setEditFlexTemplatesError] = useState("");
  const [availableFlexTemplates, setAvailableFlexTemplates] = useState<{ value: string; label: string }[]>([]);
  const [tripOpen, setTripOpen] = useState(false);
  const [fuelOpen, setFuelOpen] = useState(false);
  const [flexOpen, setFlexOpen] = useState(false);

  // WhatsApp Assistant modal state
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [targetLinkUser, setTargetLinkUser] = useState<{ uid: string; name: string } | null>(null);
  const [localWhatsappIdentities, setLocalWhatsappIdentities] = useState<WhatsAppIdentityEntry[]>([]);
  const [unlinkingWhatsApp, setUnlinkingWhatsApp] = useState(false);
  const [confirmUnlinkId, setConfirmUnlinkId] = useState<string | null>(null);

  const fetchLocalWhatsappIdentities = useCallback(async () => {
    if (!companyId) return;
    try {
      const q = query(
        collection(db, "whatsappUserIdentities"),
        where("companyId", "==", companyId)
      );
      const snapshot = await getDocs(q);
      if (snapshot && snapshot.docs) {
        const list: WhatsAppIdentityEntry[] = [];
        snapshot.docs.forEach((doc) => {
          list.push({ id: doc.id, ...(doc.data() as any) });
        });
        setLocalWhatsappIdentities(list);
      }
    } catch (err) {
      console.error("Error fetching local whatsapp identities:", err);
    }
  }, [companyId]);

  useEffect(() => {
    if (whatsappIdentities === undefined && companyId) {
      fetchLocalWhatsappIdentities();
    }
  }, [companyId, whatsappIdentities, fetchLocalWhatsappIdentities]);

  const effectiveWhatsappIdentities =
    whatsappIdentities !== undefined ? whatsappIdentities : localWhatsappIdentities;

  const handleUnlinkWhatsApp = async (identityId: string) => {
    setUnlinkingWhatsApp(true);
    try {
      await deleteDoc(doc(db, "whatsappUserIdentities", identityId));
      setMessage("WhatsApp number unlinked successfully.");
      setConfirmUnlinkId(null);
      if (onWhatsappIdentitiesChange) {
        onWhatsappIdentitiesChange();
      }
      fetchLocalWhatsappIdentities();
    } catch (err: any) {
      console.error("Failed to unlink WhatsApp number:", err);
      setErrorMessage(err?.message || "Failed to unlink WhatsApp number.");
    } finally {
      setUnlinkingWhatsApp(false);
    }
  };

  // Deletion modal state
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [deleteConfirmationInput, setDeleteConfirmationInput] = useState("");
  const [isDeletingUser, setIsDeletingUser] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  useEffect(() => {
    const fetchTrips = async () => {
      setIsOperatorsFetched(true);
      const operatorSnapshots = await getDocs(
        query(
          collection(db, "fleetOperators"),
          where("companyId", "==", companyId)
        )
      );
      if (operatorSnapshots && operatorSnapshots.size > 0) {
        const ops: FleetOperatorEntry[] = [];
        operatorSnapshots.forEach((doc) =>
          ops.push({ id: doc.id, fullName: (doc.data().fullName as string) || "Unnamed" })
        );
        ops.sort((a, b) => a.fullName.localeCompare(b.fullName));
        const entries: FleetOperatorEntry[] = [{ id: null, fullName: "Unassigned" }, ...ops];
        setFleetOperators(entries);
      }
    };
    if (!isOperatorsFetched) {
      fetchTrips();
    }
  }, [companyId, isOperatorsFetched]);

  // Fetch available flex form templates
  useEffect(() => {
    const fetchFlexTemplates = async () => {
      if (!companyId) return;
      try {
        const q = query(collection(db, "flexFormTemplates"), where("companyId", "==", companyId), where("archived", "!=", true));
        const snapshot = await getDocs(q);
        if (snapshot && snapshot.docs) {
          const templates = snapshot.docs.map(doc => ({ value: doc.id, label: (doc.data().name as string) || doc.id }));
          templates.sort((a, b) => a.label.localeCompare(b.label));
          setAvailableFlexTemplates(templates);
        }
      } catch (error) {
        console.error("Error fetching flex templates:", error);
      }
    };
    fetchFlexTemplates();
  }, [companyId]);

  const handleAdminPromotion = (uid: string, name: string) => {
    if (!uid) {
      setErrorMessage("Cannot promote user: no linked Firebase Auth profile (UID missing).");
      return;
    }
    setLoading(true);
    upgradeToAdmin(uid)
      .then(() => {
        setMessage(
          `Successfully promoted ${name} to 'Admin'. It may take up to a minute for permissions to be set.`
        );
        updateCallback();
      })
      .catch((err) => {
        console.error("Admin promotion failed:", err);
        setErrorMessage(err?.message || "Promotion failed. Please try again.");
      }).finally(() => {
        setLoading(false);
      });
  };

  const handleUserDemotion = (uid: string, name: string) => {
    if (!uid) {
      setErrorMessage("Cannot demote user: no linked Firebase Auth profile (UID missing).");
      return;
    }
    setLoading(true);
    downgradeToUser(uid)
      .then(() => {
        setMessage(
          `Successfully demoted ${name} to 'User'. It may take up to a minute for permissions to be set.`
        );
        updateCallback();
      })
      .catch((err) => {
        console.error("User demotion failed:", err);
        setErrorMessage(err?.message || "Demotion failed. Please try again.");
      }).finally(() => {
        setLoading(false);
      });
  };

  const handleUserAssociation = (userElement: User, fleetOperatorId: string | null) => {
    setLoading(true);
    associateUserWithFleetOperator(userElement.uid, fleetOperatorId || "")
      .then(() => {
        updateCallback();
        setMessage("Successfully associated user with fleet operator.");
      })
      .catch((e: any) => {
        userElement.fleetOperatorId = undefined;
        setErrorMessage(`Association failed. ${e.message}`);
      }).finally(() => {
        setLoading(false);
      });
  };

  const openEditModal = async (user: User) => {
    setEditingUser(user);
    setEditFleetOperator(user.fleetOperatorId || null);
    setEditModalOpen(true);
    setEditUserTemplatesLoading(true);
    setEditUserTemplatesError("");
    setEditFlexTemplatesLoading(true);
    setEditFlexTemplatesError("");
    try {
      const userRef = doc(db, "users", user.id);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        setEditUserTemplates(userData.selectedTemplates?.captivaFuel || []);
        setEditFlexTemplates(
          userData.selectedTemplates?.captivaFlex ||
          userData.selectedTemplates?.flexForms ||
          []
        );
      } else {
        setEditUserTemplates([]);
        setEditFlexTemplates([]);
      }
    } catch (err) {
      setEditUserTemplatesError("Failed to load user templates.");
      setEditUserTemplates([]);
      setEditFlexTemplatesError("Failed to load flex templates.");
      setEditFlexTemplates([]);
    } finally {
      setEditUserTemplatesLoading(false);
      setEditFlexTemplatesLoading(false);
    }
  };

  useEffect(() => {
    const editId = searchParams.get('editId');
    if (editId) {
      const match = elements.find((el) => el.id === editId);
      if (match && !editModalOpen) {
        openEditModal(match);
      }
    }
  }, [searchParams, elements]);

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingUser(null);
    setEditFleetOperator(null);
    setConfirmUnlinkId(null);
    setDeleteConfirmOpened(false);
    setDeleteConfirmationInput("");
    setDeleteError("");
    if (searchParams.get('editId')) {
      searchParams.delete('editId');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const openDeleteConfirm = () => {
    setDeleteConfirmationInput("");
    setDeleteError("");
    setDeleteConfirmOpened(true);
  };

  const handleDeleteUser = async () => {
    if (!editingUser || (!editingUser.uid && !editingUser.id)) return;
    setIsDeletingUser(true);
    setDeleteError("");
    try {
      await deleteUser({ uid: editingUser.uid || undefined, id: editingUser.id });
      setMessage(`Successfully deleted ${editingUser.firstName || "user"}.`);
      setDeleteConfirmOpened(false);
      setDeleteConfirmationInput("");
      closeEditModal();
      updateCallback();
    } catch (err: any) {
      setDeleteError(err?.message || "Failed to delete user.");
    } finally {
      setIsDeletingUser(false);
    }
  };

  const handleSaveUserTemplates = async () => {
    if (!editingUser) return;
    setEditUserTemplatesSaving(true);
    setEditUserTemplatesError("");
    try {
      const userRef = doc(db, "users", editingUser.id);
      await updateDoc(userRef, {
        "selectedTemplates.captivaFuel": editUserTemplates
      });
      setMessage("User templates saved!");
    } catch (err) {
      setEditUserTemplatesError("Failed to save user templates.");
    } finally {
      setEditUserTemplatesSaving(false);
    }
  };

  const handleSaveFlexTemplates = async () => {
    if (!editingUser) return;
    setEditFlexTemplatesSaving(true);
    setEditFlexTemplatesError("");
    try {
      const userRef = doc(db, "users", editingUser.id);
      await updateDoc(userRef, {
        "selectedTemplates.captivaFlex": editFlexTemplates,
        "selectedTemplates.flexForms": editFlexTemplates,
      });
      setMessage("Flex templates saved!");
    } catch (err) {
      setEditFlexTemplatesError("Failed to save flex templates.");
    } finally {
      setEditFlexTemplatesSaving(false);
    }
  };

  const handleEditSave = async () => {
    if (editingUser && editFleetOperator !== (editingUser.fleetOperatorId || null)) {
      await handleUserAssociation(editingUser, editFleetOperator);
    }
    closeEditModal();
  };

  const isOwnerUser = (user: User) => {
    if (!companyOwner) return false;
    return (
      user.uid === companyOwner ||
      user.id === companyOwner ||
      Boolean(user.email && user.email === companyOwner)
    );
  };

  const rows = elements.map((element: User) => (
    <Fragment key={element.id}>
      <MediaQuery largerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>
            <Group spacing={6} noWrap align="center">
              <span>{element?.firstName} {element?.lastName}</span>
              {isOwnerUser(element) && (
                <Tooltip label="Company Owner" withArrow withinPortal position="top">
                  <ThemeIcon size="xs" radius="xl" color="yellow" variant="light" style={{ cursor: "default" }}>
                    <Crown size={12} color="#f59f00" />
                  </ThemeIcon>
                </Tooltip>
              )}
            </Group>
          </td>
          {/* New column for template count */}
          <td style={{ textAlign: 'center' }}>
            {element.selectedTemplates?.captivaFuel?.length || 0}
          </td>
          <td style={{ textAlign: 'center' }}>
            {element.selectedTemplates?.captivaFlex?.length || 0}
          </td>
          <td>{fleetOperators.find((f) => f.id === element.fleetOperatorId)?.fullName || "Unassigned"}</td>
          <td>
            <Button
              size="sm"
              variant="outline"
              color="cap-blue"
              onClick={() => openEditModal(element)}
              style={{ minHeight: 36 }}
            >
              Configure
            </Button>
          </td>
        </tr>
      </MediaQuery>
      <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
        <tr>
          <td>
            <Group spacing={6} noWrap align="center">
              <span>{element?.firstName} {element?.lastName}</span>
              {isOwnerUser(element) && (
                <Tooltip label="Company Owner" withArrow withinPortal position="top">
                  <ThemeIcon size="xs" radius="xl" color="yellow" variant="light" style={{ cursor: "default" }}>
                    <Crown size={12} color="#f59f00" />
                  </ThemeIcon>
                </Tooltip>
              )}
            </Group>
          </td>
          {/* New column for template count */}
          <td style={{ textAlign: 'center' }}>
            {element.selectedTemplates?.captivaFuel?.length || "all"}
          </td>
          <td style={{ textAlign: 'center' }}>
            {element.selectedTemplates?.captivaFlex?.length || "all"}
          </td>
          <td>
            {element?.admin ? (
              <Badge color="cap-navy" variant="filled" size="sm" styles={{ root: { textTransform: "none", fontWeight: 600 } }}>
                Admin
              </Badge>
            ) : (
              <Badge color="gray" variant="light" size="sm" styles={{ root: { textTransform: "none" } }}>
                User
              </Badge>
            )}
          </td>
          <td>{fleetOperators.find((f) => f.id === element.fleetOperatorId)?.fullName || "Unassigned"}</td>
          <td>
            <Button
              size="sm"
              variant="outline"
              color="cap-blue"
              onClick={() => openEditModal(element)}
            >
              Configure
            </Button>
          </td>
        </tr>
      </MediaQuery>
    </Fragment>
  ));

  return (
    <>
      <Table width={"100%"} mx={0}>
        <MediaQuery largerThan="sm" styles={{ display: "none" }}>
          <thead>
            <tr>
              <th>Name</th>
              <th style={{ textAlign: 'center' }}>Fuel Templates</th>
              <th style={{ textAlign: 'center' }}>Flex Templates</th>
              <th>Fleet Operator</th>
              <th> </th>
            </tr>
          </thead>
        </MediaQuery>
        <MediaQuery smallerThan="sm" styles={{ display: "none" }}>
          <thead>
            <tr>
              <th>Name</th>
              <th style={{ textAlign: 'center' }}>Fuel Templates</th>
              <th style={{ textAlign: 'center' }}>Flex Templates</th>
              <th>Role</th>
              <th>Fleet Operator</th>
              <th> </th>
            </tr>
          </thead>
        </MediaQuery>
        <tbody>{rows}</tbody>
      </Table>
      <Modal
        opened={editModalOpen}
        size={500}
        onClose={closeEditModal}
        title={`${editingUser?.firstName} ${editingUser?.lastName}`}
        centered
      >
        {editingUser && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <b>User access level</b>
              <i style={{ fontStyle: "italic", fontSize: "smaller", marginLeft: "0.5em" }}>Admins can access the Dashboard & edit ssets</i>
              {editingUser.admin ? (
                <Button
                  color="cap-navy"
                  size="sm"
                  variant="outline"
                  fullWidth
                  disabled={!editingUser.uid}
                  onClick={() => { handleUserDemotion(editingUser.uid, editingUser.firstName); closeEditModal(); }}
                >
                  Demote to User
                </Button>
              ) : (
                <Button
                  size="sm"
                  color="cap-navy"
                  fullWidth
                  disabled={!editingUser.uid}
                  onClick={() => { handleAdminPromotion(editingUser.uid, editingUser.firstName); closeEditModal(); }}
                >
                  Promote to Admin
                </Button>
              )}
              {!editingUser.uid && (
                <Text size="xs" color="dimmed" align="center">
                  User role cannot be changed: this account lacks a linked Firebase Auth profile.
                </Text>
              )}
            </div>
            {editingUser.admin && (() => {
              const targetEditingUid = editingUser.uid || editingUser.id;
              const editingUserIdentities = effectiveWhatsappIdentities.filter(
                (item) => item.uid === targetEditingUid && item.verified === true
              );
              const isEditingUserLinked = editingUserIdentities.length > 0;

              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <Group position="apart">
                    <b>WhatsApp Assistant</b>
                    {isEditingUserLinked && (
                      <Badge color="teal" variant="light" leftSection={<IconBrandWhatsapp size={14} />} data-testid="user-whatsapp-linked-badge">
                        Linked
                      </Badge>
                    )}
                  </Group>
                  {isEditingUserLinked ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {editingUserIdentities.map((identity) => (
                        <div
                          key={identity.id}
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 8,
                            padding: 10,
                            backgroundColor: "#f8f9fa",
                            borderRadius: 6,
                            border: "1px solid #e9ecef",
                          }}
                        >
                          <Text size="sm" color="dimmed">
                            Linked phone number: <b style={{ color: "#212529" }}>{identity.phone}</b>
                          </Text>
                          {confirmUnlinkId === identity.id ? (
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                                padding: 8,
                                backgroundColor: "#fff5f5",
                                borderRadius: 4,
                                border: "1px solid #ffc9c9",
                              }}
                            >
                              <Text size="xs" color="red" weight={500}>
                                Unlink <b>{identity.phone}</b>? This user will lose WhatsApp AI Assistant access.
                              </Text>
                              <Group spacing="xs" position="right">
                                <Button
                                  size="xs"
                                  variant="default"
                                  onClick={() => setConfirmUnlinkId(null)}
                                  disabled={unlinkingWhatsApp}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="xs"
                                  color="red"
                                  loading={unlinkingWhatsApp}
                                  onClick={() => handleUnlinkWhatsApp(identity.id)}
                                  data-testid="confirm-unlink-whatsapp-btn"
                                >
                                  Yes, Unlink
                                </Button>
                              </Group>
                            </div>
                          ) : (
                            <Button
                              color="red"
                              variant="outline"
                              size="sm"
                              style={{ minHeight: 36 }}
                              leftIcon={<IconTrash size={16} />}
                              onClick={() => setConfirmUnlinkId(identity.id)}
                              disabled={unlinkingWhatsApp}
                              data-testid="user-row-whatsapp-unlink-btn"
                            >
                              Unlink WhatsApp Number
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <i style={{ fontStyle: "italic", fontSize: "smaller", marginLeft: "0.5em" }}>
                        Link personal WhatsApp phone number for AI assistant access
                      </i>
                      <Button
                        color="teal"
                        size="sm"
                        variant="outline"
                        leftIcon={<IconBrandWhatsapp size={16} />}
                        fullWidth
                        disabled={!editingUser.uid}
                        onClick={() => {
                          setTargetLinkUser({
                            uid: editingUser.uid || editingUser.id,
                            name: `${editingUser.firstName || ""} ${editingUser.lastName || ""}`.trim() || "Admin",
                          });
                          setLinkModalOpen(true);
                        }}
                        data-testid="user-row-whatsapp-link-btn"
                      >
                        Link WhatsApp Assistant
                      </Button>
                    </>
                  )}
                </div>
              );
            })()}
            <Divider my="sm" />
            <div style={{ border: '1px solid #e9ecef', borderRadius: 4, marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, cursor: 'pointer' }} onClick={() => setTripOpen(!tripOpen)}>
                <Image
                  src="/captiva-trip-icon.png"
                  height={40}
                  width={40}
                />
                <b>Trip Configurations</b>
                <div style={{ marginLeft: 'auto' }}>{tripOpen ? '▼' : '▶'}</div>
              </div>
              <Collapse in={tripOpen}>
                <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                  <i style={{ fontStyle: "italic", fontSize: "smaller", marginLeft: "0.5em" }}>Only access Trips assigned to a Fleet Operator</i>
                  <Select
                    label="Fleet Operator"
                    placeholder="Unassigned"
                    searchable
                    clearable
                    nothingFound="No fleet operators found..."
                    value={editFleetOperator}
                    onChange={setEditFleetOperator}
                    data={fleetOperators.map((config) => ({ value: config.id || "", label: config.fullName }))}
                  />
                  <Button
                    mt="md"
                    color="cap-navy"
                    onClick={handleEditSave}
                    disabled={editFleetOperator === (editingUser?.fleetOperatorId || null)}
                  >
                    Save
                  </Button>
                </div>
              </Collapse>
            </div>
            <div style={{ border: '1px solid #e9ecef', borderRadius: 4, marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, cursor: 'pointer' }} onClick={() => setFuelOpen(!fuelOpen)}>
                <Image
                  src="/captiva-fuel-icon.png"
                  height={40}
                  width={40}
                />
                <b>Fuel Configurations</b>
                <div style={{ marginLeft: 'auto' }}>{fuelOpen ? '▼' : '▶'}</div>
              </div>
              <Collapse in={fuelOpen}>
                <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                  <i style={{ fontStyle: "italic", fontSize: "smaller", marginLeft: "0.5em" }}>Only do specific fueling activities</i>
                  {editUserTemplatesLoading ? (
                    <Loader size="xs" />
                  ) : (
                    <MultiSelect
                      label="Available Templates"
                      placeholder="Access to all templates"
                      data={templateOptions}
                      value={editUserTemplates}
                      onChange={setEditUserTemplates}
                      clearable
                      searchable
                      nothingFound="No templates found..."
                      style={{ marginBottom: '1em' }}
                    />
                  )}
                  <Button
                    onClick={handleSaveUserTemplates}
                    loading={editUserTemplatesSaving}
                    disabled={editUserTemplatesSaving || editUserTemplatesLoading}
                    color="cap-navy"
                    mt="xs"
                  >
                    Save User Templates
                  </Button>
                  {editUserTemplatesError && (
                    <Notification color="red" title="Error" mt="xs" onClose={() => setEditUserTemplatesError("")}>{editUserTemplatesError}</Notification>
                  )}
                </div>
              </Collapse>
            </div>
            <div style={{ border: '1px solid #e9ecef', borderRadius: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 8, cursor: 'pointer' }} onClick={() => setFlexOpen(!flexOpen)}>
                <Image
                  src="/captiva-flex.png"
                  height={40}
                  width={40}
                />
                <b>Flex Configurations</b>
                <div style={{ marginLeft: 'auto' }}>{flexOpen ? '▼' : '▶'}</div>
              </div>
              <Collapse in={flexOpen}>
                <div style={{ padding: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                  <i style={{ fontStyle: "italic", fontSize: "smaller", marginLeft: "0.5em" }}>Only access specific flex form templates</i>
                  {editFlexTemplatesLoading ? (
                    <Loader size="xs" />
                  ) : (
                    <MultiSelect
                      label="Available Flex Templates"
                      placeholder="Access to all templates"
                      data={availableFlexTemplates}
                      value={editFlexTemplates}
                      onChange={setEditFlexTemplates}
                      clearable
                      searchable
                      nothingFound="No flex templates found..."
                      style={{ marginBottom: '1em' }}
                    />
                  )}
                  <Button
                    onClick={handleSaveFlexTemplates}
                    loading={editFlexTemplatesSaving}
                    disabled={editFlexTemplatesSaving || editFlexTemplatesLoading}
                    color="cap-navy"
                    mt="xs"
                  >
                    Save Flex Templates
                  </Button>
                  {editFlexTemplatesError && (
                    <Notification color="red" title="Error" mt="xs" onClose={() => setEditFlexTemplatesError("")}>{editFlexTemplatesError}</Notification>
                  )}
                </div>
              </Collapse>
            </div>
            {isCompanyOwner && (editingUser.uid ? editingUser.uid !== userId : editingUser.id !== userId) && (
              <>
                <Divider my="sm" />
                <div
                  style={{
                    border: "1px solid #ffc9c9",
                    backgroundColor: "#fff5f5",
                    borderRadius: 4,
                    padding: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                  }}
                >
                  <Text size="sm" weight={600} color="red">
                    Danger Zone
                  </Text>
                  <Text size="xs" color="dimmed">
                    Permanently delete this user from your company. This will disable login, revoke active sessions, and unassign any driver operator profile.
                  </Text>
                  <Button
                    color="red"
                    variant="outline"
                    size="sm"
                    style={{ minHeight: 36 }}
                    onClick={openDeleteConfirm}
                  >
                    Delete User
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
      <Modal
        opened={deleteConfirmOpened}
        onClose={() => {
          if (!isDeletingUser) {
            setDeleteConfirmOpened(false);
            setDeleteConfirmationInput("");
            setDeleteError("");
          }
        }}
        title={
          <Text weight={700} color="red">
            Confirm User Deletion
          </Text>
        }
        centered
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Text size="sm">
            Are you sure you want to delete{" "}
            <b>
              {editingUser?.firstName} {editingUser?.lastName}
            </b>
            ?
          </Text>
          <Text size="xs" color="dimmed">
            This action will disable the user account, terminate all active sessions, and unassign any associated fleet operator profile.
          </Text>
          <Text size="xs" weight={500}>
            Please type <b style={{ color: "red" }}>delete</b> below to confirm:
          </Text>
          <TextInput
            placeholder="Type 'delete' to confirm"
            value={deleteConfirmationInput}
            onChange={(e) => setDeleteConfirmationInput(e.currentTarget.value)}
            disabled={isDeletingUser}
            data-autofocus
          />
          {deleteError && (
            <Notification
              color="red"
              title="Deletion Failed"
              disallowClose
            >
              {deleteError}
            </Notification>
          )}
          <Group position="right" mt="xs">
            <Button
              variant="default"
              color="gray"
              size="sm"
              style={{ minHeight: 36 }}
              onClick={() => {
                setDeleteConfirmOpened(false);
                setDeleteConfirmationInput("");
                setDeleteError("");
              }}
              disabled={isDeletingUser}
            >
              Cancel
            </Button>
            <Button
              color="red"
              size="sm"
              style={{ minHeight: 36 }}
              loading={isDeletingUser}
              disabled={deleteConfirmationInput.trim().toLowerCase() !== "delete" || isDeletingUser}
              onClick={handleDeleteUser}
            >
              Delete User
            </Button>
          </Group>
        </div>
      </Modal>
      <WhatsAppLinkModal
        opened={linkModalOpen}
        onClose={() => {
          setLinkModalOpen(false);
          setTargetLinkUser(null);
          fetchLocalWhatsappIdentities();
          if (onWhatsappIdentitiesChange) {
            onWhatsappIdentitiesChange();
          }
        }}
        companyId={companyId}
        userId={targetLinkUser?.uid || userId || ""}
        userName={targetLinkUser?.name}
        botPhoneNumber="+14704943044"
      />
      <div style={{ position: "relative" }}>
        {message && (
          <Notification
            icon={<Check size={18} />}
            color="teal"
            title="Success"
            style={{ position: "fixed", bottom: "20px", right: "20px", width: "500px" }}
            onClose={() => setMessage("")}
          >
            {message}
          </Notification>
        )}
        {errorMessage && (
          <Notification
            icon={<Flag size={18} />}
            color="red"
            title="Error"
            style={{ position: "fixed", bottom: "20px", right: "20px", width: "500px" }}
            onClose={() => setErrorMessage("")}
          >
            {errorMessage}
          </Notification>
        )}
      </div>
    </>
  );
}

export interface UsersProps {
  companyId: string;
  auth: {
    claims: {
      user_id?: string;
      admin?: boolean;
      companyId?: string;
    };
  } | null | undefined;
}

export function Users({ companyId, auth }: UsersProps) {
  const [message, setMessage] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchedUsers, setFetchedUsers] = useState(false);
  const [userId, setUserId] = useState<string | undefined>(undefined);

  const [isCompanyOwner, setIsCompanyOwner] = useState(false);
  const [companyOwner, setCompanyOwner] = useState<string | null>(null);
  const isAdmin = Boolean(auth?.claims?.admin);
  const [headerLinkModalOpen, setHeaderLinkModalOpen] = useState(false);
  const [whatsappIdentities, setWhatsappIdentities] = useState<WhatsAppIdentityEntry[]>([]);

  const fetchWhatsappIdentities = useCallback(async () => {
    if (!companyId) return;
    try {
      const q = query(
        collection(db, "whatsappUserIdentities"),
        where("companyId", "==", companyId)
      );
      const snap = await getDocs(q);
      if (snap && snap.docs) {
        const list: WhatsAppIdentityEntry[] = [];
        snap.docs.forEach((doc) => {
          list.push({ id: doc.id, ...(doc.data() as any) });
        });
        setWhatsappIdentities(list);
      }
    } catch (err) {
      console.error("Failed to fetch whatsapp identities in Users:", err);
    }
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    const q = query(
      collection(db, "whatsappUserIdentities"),
      where("companyId", "==", companyId)
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        if (snapshot && snapshot.docs) {
          const list: WhatsAppIdentityEntry[] = [];
          snapshot.docs.forEach((d) => {
            list.push({ id: d.id, ...(d.data() as any) });
          });
          setWhatsappIdentities(list);
        }
      },
      (err) => {
        console.error("Error listening to whatsapp identities in Users:", err);
      }
    );
    fetchWhatsappIdentities();
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [companyId, fetchWhatsappIdentities]);

  const currentAuthUserId = userId || auth?.claims?.user_id;
  const isCurrentUserLinked = useMemo(() => {
    if (!currentAuthUserId) return false;
    return whatsappIdentities.some(
      (item) => item.uid === currentAuthUserId && item.verified === true
    );
  }, [whatsappIdentities, currentAuthUserId]);

  useEffect(() => {
    if (auth?.claims) {
      setUserId(auth?.claims?.user_id);
    }
  }, [auth]);

  useEffect(() => {
    if (companyId && userId) {
      const fetchOwner = async () => {
        try {
          const companySnap = await getDoc(doc(db, "companies", companyId));
          if (companySnap.exists()) {
            const owner = companySnap.data()?.owner || companySnap.data()?.ownerUid;
            setCompanyOwner(owner || null);
            const callerEmail = (auth?.claims as any)?.email;
            setIsCompanyOwner(Boolean(owner && (owner === userId || owner === callerEmail)));
          }
        } catch (err) {
          console.error("Failed to fetch company owner status:", err);
        }
      };
      fetchOwner();
    }
  }, [companyId, userId, auth]);

  const handleResults = (
    documentSnapshots: QuerySnapshot<DocumentData>,
    setter: React.Dispatch<React.SetStateAction<User[]>>
  ) => {
    const entries: User[] = [];

    if (documentSnapshots && documentSnapshots.forEach) {
      documentSnapshots.forEach((doc) => {
        const data = doc.data();
        if (!data.isDeleted) {
          entries.push({ id: doc.id, ...data } as User);
        }
      });
    }

    entries.sort((a, b) => {
      const nameA = `${a.firstName || ""} ${a.lastName || ""}`.trim().toLowerCase();
      const nameB = `${b.firstName || ""} ${b.lastName || ""}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });

    setter(entries);
  };

  const buildQuery = (templateName: string) => {
    return query(
      collection(db, templateName),
      where("companyId", "==", companyId)
    );
  };

  useEffect(() => {
    if (!fetchedUsers && companyId && userId) {
      const fetchUsers = async () => {
        setFetchedUsers(true);
        setLoading(true);

        const documentSnapshots = await getDocs(buildQuery("users"));

        handleResults(documentSnapshots, setUsers);
        setLoading(false);
      };
      fetchUsers();
    }
  }, [companyId, userId, fetchedUsers]);

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const term = searchTerm.toLowerCase();
    return users.filter((user) => {
      const fullName = `${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
      const email = (user.email || "").toLowerCase();
      return fullName.includes(term) || email.includes(term);
    });
  }, [users, searchTerm]);

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Manage company members, access roles, and invite codes"
        action={
          <Group spacing="xs">
            <TextInput
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.currentTarget.value)}
              style={{ width: "300px" }}
              data-testid="users-search-input"
            />
            {isAdmin && !isCurrentUserLinked && (
              <Button
                color="teal"
                leftIcon={<IconBrandWhatsapp size={16} />}
                onClick={() => setHeaderLinkModalOpen(true)}
                data-testid="header-link-whatsapp-btn"
              >
                Link WhatsApp
              </Button>
            )}
          </Group>
        }
      />
      <Space h="sm" />
      <Grid columns={24}>
        {loading ? (
          <Loader style={{ margin: "auto", width: "100%" }}></Loader>
        ) : (
          <>
            {filteredUsers && filteredUsers?.length ? (
              <UserTable
                elements={filteredUsers}
                updateCallback={() => {
                  setFetchedUsers(false);
                  fetchWhatsappIdentities();
                }}
                userId={userId}
                companyId={companyId}
                isCompanyOwner={isCompanyOwner}
                companyOwner={companyOwner}
                isAdmin={isAdmin}
                whatsappIdentities={whatsappIdentities}
                onWhatsappIdentitiesChange={fetchWhatsappIdentities}
              />
            ) : (
              searchTerm && (
                <Text size="sm" color="dimmed" style={{ margin: "1em auto", width: "100%", textAlign: "center" }}>
                  No users match your search.
                </Text>
              )
            )}
          </>
        )}
        <Divider my="sm" />
      </Grid>
      {message && (
        <Notification
          icon={<InfoCircle size={18} />}
          color="cap-blue"
          title="Info"
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            width: "500px",
            zIndex: 1000,
          }}
          onClose={() => setMessage("")}
        >
          {message}
        </Notification>
      )}
      <WhatsAppLinkModal
        opened={headerLinkModalOpen}
        onClose={() => {
          setHeaderLinkModalOpen(false);
          fetchWhatsappIdentities();
        }}
        companyId={companyId}
        userId={userId || auth?.claims?.user_id || ""}
        userName="Admin"
        botPhoneNumber="+14704943044"
      />
    </>
  );
}
